import { useEffect, useRef } from "react";
import { HubSelectionCard } from "./HubSelectionCard";
import { HubListItemWithDistance } from "./hubFilters";

export type HubTrackActionRenderer = (hub: { id: string; name: string }, close: () => void) => React.ReactNode;

export type HubTrackProps = {
	hubs: readonly HubListItemWithDistance[];
	renderAction: HubTrackActionRenderer;
	isOpen?: boolean;
	close?: () => void;
} & (
	| {
			selectedHubId: string | undefined;
			setSelectedHubId: (hubId: string) => void;
	  }
	| { selectedHubId?: never; setSelectedHubId?: never }
);

export const HubTrack = (props: HubTrackProps) => {
	const { hubs, renderAction, selectedHubId, setSelectedHubId, close, isOpen } = props;
	const containerRef = useRef<HTMLDivElement>(null);
	const hubRefs = useRef<Map<string, HTMLDivElement>>(new Map());
	const hasUserScrolledRef = useRef(false);
	const isUserScrollingRef = useRef(false);
	const scrollIdleTimeoutRef = useRef<number | null>(null);

	useEffect(() => {
		if (!setSelectedHubId) return;

		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries
					.filter((e) => e.isIntersecting)
					.sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
				if (!entry) return;
				const hubId = entry.target.getAttribute("data-hub-id");
				if (!hubId) return;

				if (hasUserScrolledRef.current) {
					setSelectedHubId?.(hubId);
				}
			},
			{
				root: containerRef.current,
				threshold: [0.25, 0.5, 0.75],
				rootMargin: "0px",
			}
		);

		hubRefs.current.forEach((element) => {
			observer.observe(element);
		});

		return () => {
			observer.disconnect();
		};
	}, [hubs, setSelectedHubId]);

	useEffect(() => {
		hasUserScrolledRef.current = false;
		isUserScrollingRef.current = false;

		if (scrollIdleTimeoutRef.current !== null) {
			window.clearTimeout(scrollIdleTimeoutRef.current);
			scrollIdleTimeoutRef.current = null;
		}
	}, [hubs, isOpen]);

	useEffect(() => {
		if (!isOpen || !selectedHubId) {
			return;
		}

		if (isUserScrollingRef.current) {
			return;
		}

		const selectedHubElement = hubRefs.current.get(selectedHubId);
		if (!selectedHubElement) {
			return;
		}

		const frameId = requestAnimationFrame(() => {
			selectedHubElement.scrollIntoView({ behavior: "auto", block: "nearest", inline: "center" });
		});

		return () => {
			cancelAnimationFrame(frameId);
		};
	}, [hubs, isOpen, selectedHubId]);

	useEffect(() => {
		return () => {
			if (scrollIdleTimeoutRef.current !== null) {
				window.clearTimeout(scrollIdleTimeoutRef.current);
			}
		};
	}, []);

	return (
		<div
			ref={containerRef}
			className="flex items-stretch gap-2 overflow-x-auto snap-x snap-mandatory scroll-px-6"
			onScroll={() => {
				hasUserScrolledRef.current = true;
				isUserScrollingRef.current = true;

				if (scrollIdleTimeoutRef.current !== null) {
					window.clearTimeout(scrollIdleTimeoutRef.current);
				}

				scrollIdleTimeoutRef.current = window.setTimeout(() => {
					isUserScrollingRef.current = false;
					scrollIdleTimeoutRef.current = null;
				}, 120);
			}}
		>
			{hubs.map((hub) => (
				<div
					key={hub.id}
					ref={(element) => {
						if (element) {
							hubRefs.current.set(hub.id, element);
							return;
						}

						hubRefs.current.delete(hub.id);
					}}
					data-hub-id={hub.id}
					className="flex basis-10/12 snap-start shrink-0 first:ml-6 last:mr-6"
				>
					<HubSelectionCard hub={hub} action={renderAction({ id: hub.id, name: hub.meta.name }, close ?? (() => {}))} />
				</div>
			))}
		</div>
	);
};
